import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { format, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useUrlFilters, TIMESHEETS_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import { notifyReadOnly, notifySaveFailure, errorMessage } from '../../utils/supabaseErrors';
import { formatTime, addHoursToTime, LunchBreak } from '../../utils/timesheetTime';
import SegmentedControl from '../../components/SegmentedControl';
import IconButton from '../../components/IconButton';
import { CopyIcon, EditIcon, StarIcon, TrashIcon } from '../../components/IconButton/icons';
import { useConfirmDialog } from '../../components/ConfirmDialog';
import { useFieldSaveState, FieldKey } from '../../components/ProjectDetailModal/useFieldSaveState';
import ProjectDetailModal from '../../components/ProjectDetailModal';
import { ProjectDetailTab } from '../../components/ProjectDetailModal/config';
import { useProjectDetailReopen } from '../../components/ProjectDetailModal/useProjectDetailReopen';
import { BaseTimesheetGrid, BaseTimesheetList } from '../../components/TimesheetTable';
import type { ProjectOption } from '../../components/SearchableProjectDropdown/config';
import TableSkeleton from '../../components/TableSkeleton';
import ListPage from '../../components/ListPage';
import WeekSelection from './components/WeekSelection';
import TimesheetHeader from './components/TimesheetHeader';
import DayPicker from './components/DayPicker';
import DayView from './components/DayView';
import type { DayAddSeed } from './components/DayView/DayAddRow';
import { LUNCH_FIELDS, LunchField } from './components/DayView/DayLunchRow';
import RecentProjectsStrip from './components/RecentProjectsStrip';
import EntryEditorModal from './components/EntryEditorModal';
import { useTimesheetBatch } from './useTimesheetBatch';
import {
  dateKey,
  dayLunch,
  dayTotals,
  groupEntries,
  groupFromLeave,
  groupFromOption,
  isDateInWeek,
  lastEndTime,
  weekStartKey,
  weekStats
} from './timesheetDerive';
import {
  EntryEditorDefaults,
  LeaveType,
  ProjectGroup,
  TimesheetEntry,
  TimesheetEntryInput,
  TimesheetEntryPatch,
  TimesheetView
} from './config';
import './timesheets.scss';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
}

interface EditorState {
  mode: 'create' | 'edit';
  entry?: TimesheetEntry | null;
  defaults?: EntryEditorDefaults | null;
}

const VIEW_OPTIONS: { value: TimesheetView; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
  { value: 'list', label: 'List' }
];

/**
 * The employee's timesheet. One fetch of the week's entries feeds the week
 * grid (type into cells), the day view (start/end times, chained) and the
 * list; the Recent strip puts a project one click away. Submit and reopen go
 * through the lifecycle RPCs; everything else is a plain row write that the
 * database's triggers police.
 */
const Timesheets: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();

  const { filters, setFilter, setFilters } = useUrlFilters(TIMESHEETS_FILTER_DEFINITIONS);
  const [searchParams] = useSearchParams();
  const selectedWeek = filters.week;
  const view = (['week', 'day', 'list'].includes(filters.view) ? filters.view : 'week') as TimesheetView;

  // Who am I, as a team member, and which view I starred. The starred view
  // is stored on the profile like the primary workflow type is, and applies
  // only when the URL does not name a view itself.
  const [teamMemberId, setTeamMemberId] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [preferredView, setPreferredView] = useState<TimesheetView | null>(null);
  const [savingPreference, setSavingPreference] = useState(false);
  useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('team_member_id, preferred_timesheet_view')
        .eq('id', session.user.id)
        .single();
      if (cancelled) return;
      if (error || !data?.team_member_id) {
        setProfileError('Your profile is not linked to a team member. Please contact your administrator.');
        return;
      }
      setTeamMemberId(data.team_member_id);
      const pref = data.preferred_timesheet_view as TimesheetView | null;
      setPreferredView(pref);
      if (pref && !searchParams.has('view')) setFilter('view', pref);
    })();
    return () => {
      cancelled = true;
    };
    // Runs once per sign-in: the URL check is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, session?.user?.id]);

  // Star the current view, or un-star it if it is already the preference.
  // Optimistic, reverted on failure, as the workflow preference is.
  const handleTogglePreferredView = useCallback(async () => {
    if (!session?.user?.id || savingPreference) return;
    const next = preferredView === view ? null : view;
    const previous = preferredView;
    setPreferredView(next);
    setSavingPreference(true);
    try {
      const { error } = await supabase.from('profiles').update({ preferred_timesheet_view: next }).eq('id', session.user.id);
      if (error) throw error;
    } catch (err) {
      console.error('Error saving the preferred timesheet view:', err);
      notifyReadOnly(err);
      setPreferredView(previous);
    } finally {
      setSavingPreference(false);
    }
  }, [session?.user?.id, savingPreference, preferredView, view, supabase]);

  const api = useTimesheetBatch(teamMemberId, selectedWeek);
  const { batches, batch, entries, standardHours, defaultStart, lunch, lunchFor, loadingBatches, loadingEntries } = api;

  // Default the week to the most recent batch when the URL has none (or a stale one).
  useEffect(() => {
    if (batches.length === 0) return;
    if (selectedWeek && batches.some((b) => b.week_ending_date === selectedWeek)) return;
    setFilter('week', batches[0].week_ending_date);
  }, [batches, selectedWeek, setFilter]);

  // The day view's date: the URL's if it is inside the week, else today if
  // it is, else Monday.
  const selectedDay = useMemo(() => {
    if (!selectedWeek) return '';
    if (filters.day && isDateInWeek(filters.day, selectedWeek)) return filters.day;
    const today = dateKey(new Date());
    return isDateInWeek(today, selectedWeek) ? today : weekStartKey(selectedWeek);
  }, [filters.day, selectedWeek]);

  // Derived views.
  const [blankRows, setBlankRows] = useState<ProjectGroup[]>([]);
  const [focusRowKey, setFocusRowKey] = useState<string | null>(null);
  useEffect(() => setBlankRows([]), [batch?.id]);
  const groups = useMemo(() => groupEntries(entries, blankRows), [entries, blankRows]);
  const presentKeys = useMemo(() => new Set(groups.map((g) => g.groupKey)), [groups]);
  const stats = useMemo(() => weekStats(entries), [entries]);
  const totals = useMemo(() => dayTotals(entries), [entries]);
  const dayEntries = useMemo(() => entries.filter((e) => e.date === selectedDay), [entries, selectedDay]);
  const selectedDayLunch = useMemo(() => dayLunch(entries, selectedDay, lunch), [entries, selectedDay, lunch]);

  const isDraft = batch?.status === 'Draft';
  const { mark, stateFor } = useFieldSaveState();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [addSeed, setAddSeed] = useState<DayAddSeed | null>(null);
  const [recentRefresh, setRecentRefresh] = useState(0);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);

  const reportSaveError = useCallback((err: unknown, fallback: string) => {
    console.error(fallback, err);
    notifyReadOnly(err);
    notifySaveFailure(err, errorMessage(err, fallback));
  }, []);

  // --- grid ----------------------------------------------------------------

  const cellKey = (group: ProjectGroup, date: string): FieldKey => `row:cell:${group.groupKey}:${date}`;

  const handleCellCommit = useCallback(
    async (group: ProjectGroup, date: string, hours: number | null, existing?: TimesheetEntry) => {
      const key = cellKey(group, date);
      mark(key, 'saving');
      try {
        if (hours === null) {
          if (existing) await api.deleteEntry(existing.id);
        } else if (existing) {
          await api.updateEntry(existing.id, { hours });
        } else {
          await api.createEntry({
            project_id: group.projectId,
            is_stage: group.isLeave ? null : group.entityType === 'stage',
            leave_type: group.leaveType,
            date,
            hours,
            description: null,
            start_time: null,
            end_time: null
          });
          setRecentRefresh((n) => n + 1);
        }
        mark(key, 'saved');
      } catch (err) {
        mark(key, 'error');
        reportSaveError(err, 'The hours could not be saved.');
      }
    },
    [api, mark, reportSaveError]
  );

  const handleOpenCell = useCallback(
    (group: ProjectGroup, date: string, cellEntries: TimesheetEntry[]) => {
      if (cellEntries.length === 0) {
        setEditor({ mode: 'create', defaults: { date, group } });
      } else if (cellEntries.length === 1) {
        setEditor({ mode: 'edit', entry: cellEntries[0] });
      } else {
        setFilters({ view: 'day', day: date });
      }
    },
    [setFilters]
  );

  // A row added from the grid's own search or a recent chip: it appears with
  // no entries and the keyboard lands in it.
  const addBlankRow = useCallback((group: ProjectGroup) => {
    setBlankRows((rows) => (rows.some((r) => r.groupKey === group.groupKey) ? rows : [...rows, group]));
    setFocusRowKey(group.groupKey);
  }, []);

  const handleRemoveBlankRow = useCallback((group: ProjectGroup) => {
    setBlankRows((rows) => rows.filter((r) => r.groupKey !== group.groupKey));
  }, []);

  // --- recent strip --------------------------------------------------------

  const handlePickProject = useCallback(
    (option: ProjectOption) => {
      if (view === 'day') {
        setAddSeed({ option });
        return;
      }
      if (view === 'list') {
        setEditor({ mode: 'create', defaults: { projectOption: option } });
        return;
      }
      addBlankRow(groupFromOption(option));
    },
    [view, addBlankRow]
  );

  const handlePickLeave = useCallback(
    (leaveType: LeaveType) => {
      if (view === 'day') {
        setAddSeed({ leaveType });
        return;
      }
      if (view === 'list') {
        setEditor({ mode: 'create', defaults: { leaveType } });
        return;
      }
      addBlankRow(groupFromLeave(leaveType));
    },
    [view, addBlankRow]
  );

  // --- day view ------------------------------------------------------------

  const dayFieldKey = (entryId: number, field: string): FieldKey => `row:${entryId}:${field}`;

  const handleDayPatch = useCallback(
    async (entry: TimesheetEntry, field: string, patch: TimesheetEntryPatch) => {
      const key = dayFieldKey(entry.id, field);
      mark(key, 'saving');
      try {
        await api.updateEntry(entry.id, patch);
        mark(key, 'saved');
      } catch (err) {
        mark(key, 'error');
        reportSaveError(err, 'The change could not be saved.');
      }
    },
    [api, mark, reportSaveError]
  );

  const lunchFieldKey = (date: string, field: LunchField): FieldKey => `row:lunch:${date}:${field}`;

  const handleLunchChange = useCallback(
    async (field: LunchField, next: LunchBreak | null) => {
      const key = lunchFieldKey(selectedDay, field);
      mark(key, 'saving');
      try {
        await api.setDayLunch(selectedDay, next);
        // The four lunch controls edit one value, so a save that lands
        // clears an error left on any of them by an earlier refusal.
        LUNCH_FIELDS.forEach((f) => f !== field && mark(lunchFieldKey(selectedDay, f), 'idle'));
        mark(key, 'saved');
      } catch (err) {
        mark(key, 'error');
        reportSaveError(err, 'The lunch break could not be saved.');
      }
    },
    [api, selectedDay, mark, reportSaveError]
  );

  const handleDayCreate = useCallback(
    async (input: TimesheetEntryInput) => {
      await api.createEntry(input);
      setRecentRefresh((n) => n + 1);
    },
    [api]
  );

  // --- shared entry actions ------------------------------------------------

  const handleEditorSave = useCallback(
    async (payload: TimesheetEntryInput) => {
      if (editor?.mode === 'edit' && editor.entry) {
        await api.updateEntry(editor.entry.id, payload);
      } else {
        await api.createEntry(payload);
        setRecentRefresh((n) => n + 1);
      }
    },
    [api, editor]
  );

  const handleDelete = useCallback(
    async (entry: TimesheetEntry) => {
      const ok = await confirm({
        title: 'Delete this entry?',
        message: `${entry.hours} h on ${format(parseISO(entry.date), 'EEEE d MMMM')} will be removed. This cannot be undone.`,
        confirmLabel: 'Delete',
        tone: 'danger'
      });
      if (!ok) return;
      try {
        await api.deleteEntry(entry.id);
      } catch (err) {
        reportSaveError(err, 'The entry could not be deleted.');
      }
    },
    [api, confirm, reportSaveError]
  );

  const handleDuplicate = useCallback(
    async (entry: TimesheetEntry) => {
      const hours = Number(entry.hours);
      const timed = Boolean(entry.start_time && entry.end_time);
      const start = timed ? lastEndTime(entries.filter((e) => e.date === entry.date)) ?? formatTime(entry.end_time) : null;
      try {
        await api.createEntry({
          project_id: entry.project_id,
          is_stage: entry.leave_type ? null : entry.project_type === 'stage',
          leave_type: entry.leave_type,
          date: entry.date,
          hours,
          description: null,
          start_time: start,
          end_time: start ? addHoursToTime(start, hours, lunchFor(entry.date)) : null
        });
      } catch (err) {
        reportSaveError(err, 'The entry could not be duplicated.');
      }
    },
    [api, entries, lunchFor, reportSaveError]
  );

  // --- lifecycle -----------------------------------------------------------

  const handleSubmit = useCallback(async () => {
    if (!batch) return;
    const ok = await confirm({
      title: 'Submit this week?',
      message: `${stats.total} hours go onto the job cards straight away. You can reopen the week to make changes until a manager approves it.`,
      confirmLabel: 'Submit week',
      tone: 'info'
    });
    if (!ok) return;
    setLifecycleBusy(true);
    setLifecycleError(null);
    try {
      await api.submit();
    } catch (err) {
      console.error('Error submitting timesheet:', err);
      notifyReadOnly(err);
      setLifecycleError(errorMessage(err, 'The week could not be submitted.'));
    } finally {
      setLifecycleBusy(false);
    }
  }, [api, batch, confirm, stats.total]);

  const handleReopen = useCallback(async () => {
    if (!batch) return;
    const ok = await confirm({
      title: 'Reopen this week?',
      message: 'Its hours come off the job cards until you submit it again.',
      confirmLabel: 'Reopen week',
      tone: 'warning'
    });
    if (!ok) return;
    setLifecycleBusy(true);
    setLifecycleError(null);
    try {
      await api.reopen();
    } catch (err) {
      console.error('Error reopening timesheet:', err);
      notifyReadOnly(err);
      setLifecycleError(errorMessage(err, 'The week could not be reopened.'));
    } finally {
      setLifecycleBusy(false);
    }
  }, [api, batch, confirm]);

  // --- project detail modal ------------------------------------------------

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [selectedStageInfo, setSelectedStageInfo] = useState<{ jobNumber: string; stageDescription: string; parentDescription: string } | null>(null);
  const [detailTab, setDetailTab] = useState<ProjectDetailTab | undefined>(undefined);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    if (!session) return;
    supabase
      .from('active_team_members')
      .select('id, first_name, last_name')
      .eq('type', 'Engineer')
      .order('first_name')
      .then(({ data, error }) => {
        if (error) console.error('Error fetching team members:', error);
        else setTeamMembers(data || []);
      });
  }, [supabase, session]);

  const openProject = useCallback((group: ProjectGroup) => {
    if (group.isLeave || group.projectId === null) return;
    if (group.entityType === 'stage' && group.parentProjectId) {
      setSelectedProjectId(group.parentProjectId);
      setSelectedStageId(group.projectId);
      setSelectedStageInfo({
        jobNumber: group.jobNumber ?? '',
        stageDescription: group.stageDescription ?? '',
        parentDescription: group.parentDescription ?? ''
      });
    } else {
      setSelectedProjectId(group.projectId);
      setSelectedStageId(null);
      setSelectedStageInfo(null);
    }
    setDetailTab(undefined);
    setIsDetailModalOpen(true);
  }, []);

  const openProjectFromEntry = useCallback(
    (entry: TimesheetEntry) => {
      const group = groups.find((g) => g.entries.some((e) => e.id === entry.id));
      if (group) openProject(group);
    },
    [groups, openProject]
  );

  // Returning from a document opened out of the modal. The stage title text
  // comes back with the instruction: it is derived from the timesheet row
  // that was clicked, and this week's rows may no longer include it.
  useProjectDetailReopen((reopen) => {
    setSelectedProjectId(reopen.projectId);
    setSelectedStageId(reopen.stageId ?? null);
    setSelectedStageInfo(reopen.stageInfo ?? null);
    setDetailTab(reopen.tab);
    setIsDetailModalOpen(true);
  });

  const closeDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedProjectId(null);
    setSelectedStageId(null);
    setSelectedStageInfo(null);
    setDetailTab(undefined);
  }, []);

  // --- render --------------------------------------------------------------

  if (profileError || api.error) {
    return (
      <ListPage title="Timesheets" className="timesheets-page">
        <div className="timesheets-page__error">
          <h3>Timesheet could not be loaded</h3>
          <p>{profileError ?? api.error}</p>
          <button type="button" className="pill-btn" onClick={() => window.location.reload()}>
            Refresh page
          </button>
        </div>
      </ListPage>
    );
  }

  const initialLoading = !teamMemberId || (loadingBatches && batches.length === 0) || (!batch && loadingEntries);

  if (initialLoading) {
    return (
      <ListPage title="Timesheets" className="timesheets-page" loading skeleton={{ segmentCount: 0, filterCount: 0, showAction: true }}>
        <div className="timesheets-page__skeleton-strip" />
        <TableSkeleton compact rows={6} columns={9} showHeader columnWidths={['10%', '26%', '8%', '8%', '8%', '8%', '8%', '8%', '8%']} />
      </ListPage>
    );
  }

  // The week strip is the shell's second row, full width: it is the page's
  // navigation, the way the status segments are a register's.
  const weekStrip = batches.length > 0 ? (
    <WeekSelection selectedWeek={selectedWeek} onWeekSelect={(w) => setFilters({ week: w, day: '' })} availableBatches={batches} />
  ) : undefined;

  if (batches.length === 0) {
    return (
      <ListPage title="Timesheets" className="timesheets-page">
        <p className="ui-table__empty timesheets-page__empty">
          You have no timesheet weeks yet. They are created automatically each week; if you expected one, please contact your administrator.
        </p>
      </ListPage>
    );
  }

  if (!batch) {
    return (
      <ListPage title="Timesheets" className="timesheets-page" filters={weekStrip}>
        <p className="ui-table__empty timesheets-page__empty">No timesheet for this week.</p>
      </ListPage>
    );
  }

  // The week's lifecycle action lives in the identity strip beside the
  // status pill: it belongs to the week on screen, not to the page.
  const headerActions = (
    <>
      {batch.status === 'Draft' && (
        <button type="button" className="pill-btn pill-btn--primary" onClick={handleSubmit} disabled={lifecycleBusy || entries.length === 0}>
          {lifecycleBusy ? 'Submitting…' : 'Submit week'}
        </button>
      )}
      {batch.status === 'Submitted' && (
        <button type="button" className="pill-btn pill-btn--ghost" onClick={handleReopen} disabled={lifecycleBusy}>
          {lifecycleBusy ? 'Reopening…' : 'Reopen week'}
        </button>
      )}
    </>
  );

  return (
    <ListPage title="Timesheets" className="timesheets-page" filters={weekStrip}>
      <TimesheetHeader batch={batch} stats={stats} standardHours={standardHours} actions={headerActions} />
      {lifecycleError && (
        <p className="field__error timesheets-page__lifecycle-error" role="alert">
          {lifecycleError}
        </p>
      )}

      <div className="timesheets-page__toolbar">
        <div className="timesheets-page__view">
          <SegmentedControl<TimesheetView>
            options={VIEW_OPTIONS}
            value={view}
            onChange={(v) => v && setFilter('view', v)}
            size="sm"
            aria-label="Timesheet view"
          />
          <IconButton
            icon={<StarIcon size={15} filled={preferredView === view} />}
            label={preferredView === view ? 'Default view. Click to clear' : `Make ${VIEW_OPTIONS.find((o) => o.value === view)?.label ?? 'this'} view the default`}
            size="sm"
            disabled={savingPreference}
            onClick={handleTogglePreferredView}
            className={`timesheets-page__star ${preferredView === view ? 'is-starred' : ''}`}
          />
        </div>
        {isDraft ? (
          <RecentProjectsStrip
            batchId={batch.id}
            presentKeys={presentKeys}
            refreshToken={recentRefresh}
            showSearch={view === 'list'}
            onPickProject={handlePickProject}
            onPickLeave={handlePickLeave}
          />
        ) : (
          <span className="timesheets-page__locked">
            {batch.status === 'Approved' ? 'Approved weeks cannot be changed.' : 'Reopen the week to make changes.'}
          </span>
        )}
      </div>

      {view === 'day' && (
        <DayPicker
          weekEndingDate={batch.week_ending_date}
          selectedDate={selectedDay}
          onSelect={(d) => setFilter('day', d)}
          totals={totals}
          standardHours={standardHours}
        />
      )}

      {view === 'week' && (
        <BaseTimesheetGrid
          groups={groups}
          weekEndingDate={batch.week_ending_date}
          isReadOnly={!isDraft}
          standardHoursPerDay={standardHours}
          onCellCommit={isDraft ? handleCellCommit : undefined}
          onOpenCell={handleOpenCell}
          onAddToCell={isDraft ? (group, date) => setEditor({ mode: 'create', defaults: { date, group } }) : undefined}
          onProjectClick={openProject}
          onRemoveBlankRow={isDraft ? handleRemoveBlankRow : undefined}
          onAddProject={isDraft ? (option) => addBlankRow(groupFromOption(option)) : undefined}
          focusRowKey={focusRowKey}
          onRowFocused={() => setFocusRowKey(null)}
          cellSaveState={(g, d) => stateFor(cellKey(g, d))}
        />
      )}

      {view === 'day' && (
        <DayView
          date={selectedDay}
          entries={dayEntries}
          isReadOnly={!isDraft}
          standardHours={standardHours}
          defaultStart={defaultStart}
          lunch={selectedDayLunch.lunch}
          usualLunch={lunch}
          lunchIsCustom={selectedDayLunch.isCustom}
          lunchSaveState={(field) => stateFor(lunchFieldKey(selectedDay, field))}
          onLunchChange={handleLunchChange}
          saveState={(id, field) => stateFor(dayFieldKey(id, field))}
          onPatch={handleDayPatch}
          onCreate={handleDayCreate}
          onEdit={(entry) => setEditor({ mode: 'edit', entry })}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onProjectClick={openProjectFromEntry}
          addSeed={addSeed}
          onAddSeedConsumed={() => setAddSeed(null)}
        />
      )}

      {view === 'list' && (
        <BaseTimesheetList
          entries={entries}
          renderActions={
            isDraft
              ? (entry) => (
                  <>
                    <IconButton icon={<EditIcon size={14} />} label="Edit entry" size="sm" onClick={() => setEditor({ mode: 'edit', entry })} />
                    <IconButton icon={<CopyIcon size={14} />} label="Duplicate entry" size="sm" onClick={() => handleDuplicate(entry)} />
                    <IconButton icon={<TrashIcon size={14} />} label="Delete entry" size="sm" tone="danger" onClick={() => handleDelete(entry)} />
                  </>
                )
              : undefined
          }
        />
      )}

      <EntryEditorModal
        isOpen={editor !== null}
        mode={editor?.mode ?? 'create'}
        entry={editor?.entry}
        defaults={editor?.defaults}
        weekEndingDate={batch.week_ending_date}
        lunchFor={lunchFor}
        onClose={() => setEditor(null)}
        onSave={handleEditorSave}
      />

      <ProjectDetailModal
        isOpen={isDetailModalOpen}
        projectId={selectedProjectId}
        stageId={selectedStageId}
        stageInfo={selectedStageInfo}
        initialTab={detailTab}
        onClose={closeDetailModal}
        onProjectUpdated={() => api.refetchEntries()}
        onProjectDeleted={closeDetailModal}
        teamMembers={teamMembers}
      />

      {confirmDialog}
    </ListPage>
  );
};

export default Timesheets;
